import {
  BoxGeometry,
  ConeGeometry,
  Euler,
  Group,
  Matrix4,
  type Material,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  Object3D,
  Quaternion,
  Vector2,
  Vector3,
  Color,
  BufferAttribute,
  Float32BufferAttribute,
  BufferGeometry,
  ShaderMaterial,
} from 'three'
import { BoxSizeEnum, BoxTypeEnum, type ColorType } from '../core/enums'
import { colorOf } from '../core/palette'
import { convertAxisAngle, convertPosition, unityLookRotation } from '../core/unity'
import type { Bus } from '../game/bus'
import type { BusTip } from '../game/session'
import { MergeTrace } from '../game/mergeTrace'
import type { GameSession } from '../game/session'
import {
  BUS_ARROW_HEIGHT,
  BUS_ARROW_SIZE,
  BUS_ARROW_WIDTH,
  BUS_CENTRE_Z,
  BUS_CRASH_VFX,
  BUS_CRASH_BOOST,
  BUS_FX_ANCHORS,
  BUS_FX_BOOST,
  BUS_LIGHT_RAY,
  BUS_SKID,
  BUS_PARTICLE_VFX,
  BUS_WIDTH,
  ICE_SHARD_MATERIAL,
  ICE_BREAK_VFX,
  type BusBodyTuning,
} from '../game/tuning'
import type { AssetLibrary } from './assetLibrary'
import { BodyTrace } from './bodyTrace'
import { BusFleet } from './busView'
import { sliceArrow } from './arrowSlice'
import { ParticlePool } from './particleMaterial'
import { ShardPool } from './shardPool'
import { advanceSkid, newRibbon, skidSegments, type SkidRibbon } from './skidTrail'
import { advanceIceBurst, emitIceBurst, emitterTriangles, shardDrawColour, type Shard } from './iceBurst'
 import {
  advanceParticles,
  alphaOf,
  emissionDue,
  emitParticles,
  frameUv,
  newEmitter,
  play,
  sizeOf,
  stopEmitting,
  type EmitterState,
  type Particle,
  type ParticleSpec,
} from './busParticles'
import {
  PUFF_SYSTEMS,
  advanceIceCountPuff,
  emitIceCountPuff,
  puffSpriteMatrix,
  type Puff,
} from './iceCountPuff'
import { CharacterView } from './characterView'
import { createWorldText, ICE_COUNT_STYLE, type WorldText } from './worldText'
import { CantMoveIndicator } from './cantMoveIndicator'

/**
 * ❓ PLACEHOLDER — how big the ice count is drawn.
 *
 * The prefab's `RectTransform` is 20 x 5, and there is **no scale chain** to
 * rebuild: `IceCountText` → `Ice` → `ConveyorBox (S|M|L)` are all
 * `m_LocalScale (1, 1, 1)`, and so is `Parts` over the body meshes, so the rect
 * is already in the same units as the 4-wide bus (`tests/iceCountLabel.test.ts`
 * reads that off all three prefabs). What the rect does *not* say is the glyph
 * size: `TextMeshPro.m_fontSize` / auto-size went with the stripped component,
 * and a TMP rect is only a layout box. This scale is the port's stand-in for
 * that font size. (An earlier draft of this comment quoted 0.09 — no such value
 * was ever committed; `git log -S ICE_COUNT_LABEL_SCALE` shows 0.55 from the
 * first commit.)
 *
 * Measured in the shipped browser (FRZ-5, `Baloo Da 2 ExtraBold` at the
 * canvas's 99.2 px): one digit inks 64 px tall x 50 px wide, "10" 96 px wide;
 * at 0.55 / 32 u per canvas px that is 1.10 u tall, 0.86 u wide, "10" 1.65 u —
 * on a 4.4 u shell: 1/4 tall and 1/5 wide for one digit, ~3/8 wide for two.
 * The only original evidence is the user's level-28 screenshot, described as
 * "about a third of the shell's width"; that file is not in the repo, so px/u
 * cannot be measured and the value stays ❓ (checklist §10).
 */
const ICE_COUNT_LABEL_SCALE = 0.55
import {
  ROAD_CUT,
  RoadWindow,
  ToonMaterial,
  writeRevealTween,
  type ClipMask,
} from './toonMaterial'
import { REVEAL_TWEEN_SECONDS } from './palette'
import { revealScaleYAt } from './revealBounce'
import { syncRoadCutShadow } from './roadCutShadow'

/**
 * One mesh of a bus and the four sets of materials it can draw with, indexed by
 * `ClipMask`: whole, cut at the start mouth, cut at the end mouth, cut at both.
 *
 * Built on demand — most buses never see a tunnel, and one that does uses two
 * of the four.
 */
interface BusMaterialSwap {
  mesh: Mesh
  variants: (Material | Material[] | undefined)[]
}

/**
 * One bus's `_hiddenSequence` while `PlayRevealMaterialTween` is running.
 *
 * `twins` maps each material the bus draws with (plain or road-cut) to the
 * per-bus instance the tween writes to; `sources` is the same map the other
 * way, so a mesh already holding an instance can be told apart from one
 * `setBusClipped` just reset.
 */
interface RevealSequence {
  /** Seconds since `ExitHiddenState`. */
  elapsed: number
  twins: Map<ToonMaterial, ToonMaterial>
  sources: Map<ToonMaterial, ToonMaterial>
}

/**
 * The gameplay view.
 *
 * Buses draw with the shipped art when an `AssetLibrary` is supplied, and fall
 * back to coloured boxes when it is not — the boxes were the whole view while
 * the rules were being rebuilt, and they stay useful for reading state at a
 * glance.
 *
 * Only what moves lives here. The ground, the road, the stops and their doors
 * are static dressing and are drawn by `EnvironmentView`.
 */

const BUS_HEIGHT = 4.01
const UP = new Vector3(0, 1, 0)

/**
 * Arming early costs nothing — a plane cuts only what is actually past it — and
 * it means the riders standing out over the bumper are cut with the body rather
 * than a frame later. The padding itself is `UpdateInvisibility`'s and lives on
 * the follower (`reachesRoadEndPadded`), where a headless test can reach it.
 */

/**
 * 📐 `Char.obj` spans x [-1.3552, 1.3552] — 2.7104 wide at unit scale. Used
 * only to pad the tunnel cut by a rider's own half-width; the draw size comes
 * from the prefab, not from here.
 */
const CHAR_MESH_WIDTH = 2.7104

/** Shards alive at once, across every bus. Four bursts' worth. */
const ICE_BURST_POOL = 240

/**
 * 📐 `ice_vfx`'s transform under the bus root: `m_LocalPosition (0, 4.36, 0)`
 * (`ConveyorBox (M).prefab:17638`, under `Ice` at the origin). The system's
 * origin, which `VelocityModule.radial` pushes shards away from.
 */
const ICE_VFX_LOCAL_Y = 4.36

/**
 * Sprites the countdown puff may have alive at once, across every bus.
 *
 * One puff is 58 sprites (16 + 1 + 6 + 35), so this is room for four at a time
 * — more than a level ever chips at once, and a hard ceiling either way.
 */
const COUNT_PUFF_POOL = 240

/**
 * Each particle material and the picture it samples.
 *
 * The two names differ — `snowflake_AB` draws `snowflake2` — so the pairing has
 * to be written down somewhere. It is read out of the `.mat` by
 * `buildAssets.mjs`, which is why the manifest carries both.
 */
const PUFF_TEXTURE: Record<string, string> = {
  snowflake_AB: 'snowflake2',
  glow2_ADD: 'glow2',
  cloud_2x2_soft_AB: 'cloud_2x2_soft',
  snow_AB: 'snow',
  // The bus's own systems and the crash burst (FX-5). `cloud_2x2_default_AB`
  // is **not** the `_soft` one above: it samples `cloud_2x2_hardshadow`.
  cloud_2x2_default_AB: 'cloud_2x2_hardshadow',
  glow1_ADD: 'glow1',
  circle_AB: 'circle',
  triangle_curve_AB: 'triangle_curve',
  circlestretchy_AB: 'circlestretchy',
}

/**
 * The four systems a bus carries, in the order their pools are built.
 *
 * `key` is the field on `BUS_FX_ANCHORS`; `on` decides, from the bus's own
 * state, whether the system is currently `Play()`ing — which is the whole of
 * what the rules hand the view. See `Bus.smoke` and `Bus.departFxPlaying` for
 * where each of those is set and from which decomp line.
 */
const BUS_SYSTEMS = [
  { key: 'smoke', spec: BUS_PARTICLE_VFX.smoke, on: (b: Bus) => b.smoke === 'puff' },
  { key: 'smokeTrail', spec: BUS_PARTICLE_VFX.smokeTrail, on: (b: Bus) => b.smoke === 'trail' },
  { key: 'departGlow', spec: BUS_PARTICLE_VFX.departGlow, on: (b: Bus) => b.departFxPlaying },
  { key: 'windLines', spec: BUS_PARTICLE_VFX.windLines, on: (b: Bus) => b.departFxPlaying },
] as const

/**
 * Particles alive at once per system, across every bus.
 *
 * The exhaust is the busiest: 5 per second for up to 1.5 s is about 8 per bus,
 * and a level runs a handful of buses at once. The trail is denser but shorter
 * lived. 400 is well clear of both and is a hard ceiling — overflow is dropped,
 * not queued, for the same reason the ice burst drops it: a late particle looks
 * worse than a missing one.
 */
const BUS_PARTICLE_POOL = 1600

/** Sparks alive at once. One burst is 25; this is room for several. */
const CRASH_PARTICLE_POOL = 400

const _fxMatrix = new Matrix4()
const _fxBillboard = new Matrix4()
const _fxOrigin = new Vector3()
const _fxAnchor = new Vector3()
const _fxQuat = new Quaternion()
const _fxSpin = new Matrix4()
const _capForward = new Vector3()
const _capRight = new Vector3()
const _capBasis = new Matrix4()
const _skidAt = new Vector3()
const _skidDir = new Vector3()
const _skidSide = new Vector3()

/**
 * Ribbon quads across the whole level.
 *
 * A wheel lays a point every 0.05 units and keeps 0.65 s of them; at the lot
 * speed of 24.64 u/s that is about 320 per wheel, two wheels per bus. This is
 * room for a handful of buses moving at once, which is more than a lot ever has.
 */
const SKID_QUADS = 4000

/**
 * A spec with its `startSize` multiplied — `BUS_FX_BOOST`'s size term.
 *
 * A copy rather than a mutation: the specs are the prefab's own numbers and are
 * shared by the tests that lock them against it.
 */
function scaledSpec(spec: ParticleSpec, scale: number): ParticleSpec {
  if (scale === 1) return spec
  return { ...spec, size: [spec.size[0] * scale, spec.size[1] * scale] as const }
}
const _fxColour = new Color()
const _identityQuat = new Quaternion()
const WHITE = new Color(0xffffff)

const _puffMatrix = new Matrix4()
const _puffColour = new Color()

/**
 * The one-piece body `Ice/Fake` carries, per size — the emitter surface.
 *
 * ⚠️ Exported because it has to be **asked for by name** at load time. It is the
 * one mesh the bus never draws — `DRAWN_SUBTREES` excludes `Ice`, which is what
 * PS-8 and IC-6 are about — so `BusFleet.requiredMeshes` does not collect it.
 * The particle system needs it as a **shape**, not as a drawing, and that is
 * exactly why the node exists with its renderer switched off.
 */
export const BUS_SINGLE_MESH: Readonly<Record<BoxSizeEnum, string>> = {
  [BoxSizeEnum.Small]: 'Bus_1_single',
  [BoxSizeEnum.Medium]: 'Bus_2_single',
  [BoxSizeEnum.Large]: 'Bus_3_single',
}

const _shardMatrix = new Matrix4()
const _shardScale = new Vector3()

const _pos = new Vector3()
const _tangent = new Vector3()
const _rot = new Quaternion()
const _sway = new Quaternion()
const _sharedRot = new Quaternion()
const _shakeEuler = new Euler(0, 0, 0, 'YXZ')
const _shakeRot = new Quaternion()
const DEG_TO_RAD = Math.PI / 180
const _tipPivot = new Vector3()
const _tipRot = new Quaternion()
const _bodyPose = { position: new Vector3(), tangent: new Vector3() }
const _queuePose = { position: new Vector3(), forward: new Vector3() }

/**
 * Does this bus show its pull-out flag?
 *
 * Exported because the sprite only exists once the art is loaded, so the rule
 * is otherwise unreachable from a headless test — and the rule is the part
 * that was wrong. See the call site in `sync` for the decompile.
 */
export function arrowVisible(occupiesLot: boolean, type: BoxTypeEnum): boolean {
  return occupiesLot && type !== BoxTypeEnum.Ice
}

export class SessionView {
  readonly object = new Group()
  private readonly busNodes = new Map<number, Object3D>()
  /**
   * The colour the shipped art was **built** with, per bus.
   *
   * `BusFleet.build` bakes `carMaterialName(bus.visibleColor)` into the mesh
   * tree once and never looks at it again, so a bus that changes colour after
   * it is drawn keeps the old material for ever. Only the placeholder box —
   * the fallback used when the art is not loaded — was being re-tinted.
   *
   * That made two rules invisible. `Bus.DeactivateHiddenState` reveals a hidden
   * bus while it is still parked, and `Bus.BreakIce` calls
   * `SwitchColor(_color)` when the last ice chip goes; both flip
   * `visibleColor` and neither could be seen.
   */
  private readonly builtColour = new Map<number, ColorType>()
  /**
   * Whether the shipped art was built **iced**, per bus — the second half of
   * the same key. 🔍 `Bus.ApplyIceMaterialState` puts `IceBus` on slot 0 of the
   * body renderers and `Bus.BreakIce` puts the colour back, so the material
   * follows `_type`, not only `_color`; `BreakIce` calls `SwitchColor(_color)`
   * with the colour unchanged, and a key on colour alone never rebuilt.
   */
  private readonly builtIced = new Map<number, boolean>()
  private readonly busBodies = new Map<number, Mesh>()
  private readonly disposables: { dispose(): void }[] = []
  /** The roof flag on each parked bus, when the sprite loaded. */
  private readonly busArrows = new Map<number, Object3D>()
  /** `BusController.cantMoveIndicator` (0x50) — one for the whole level. */
  private readonly cantMove: CantMoveIndicator
  /** `Bus.iceCountText` — one per iced bus, hidden when the shell is gone. */
  private readonly busIceCount = new Map<number, WorldText>()


  /**
   * 📐 The ice-break shower — `ice_vfx`, played by `Bus.BreakIce` (`:91-95`).
   *
   * Four `InstancedMesh` pools, one per `Stone_N`, because the original's
   * `ParticleSystemRenderer` is in Mesh mode with those four at equal
   * weighting. The simulation is `iceBurst.ts`; this only draws it.
   *
   * The burst is fired on an **edge**, not a level: the frame a bus's ice shell
   * goes from shown to hidden. The view has no event channel — `SessionEvents`
   * exists but nothing has ever listened to it — and every other state it draws
   * it polls, so remembering last frame's answer is the shape that fits.
   *
   * ⚠️ Capacity is `ICE_BURST_POOL` shards **in total**, not per bus. Two buses
   * thawing on the same frame share it. Overflow is dropped rather than queued:
   * a dropped shard is invisible, a queued one arrives late and looks wrong.
   */
  private iceShards: Shard[] = []
  private iceInstances: ShardPool[] = []
  /** Whether each bus was iced last frame, for the edge `BreakIce` fires on. */
  private iceWasShown = new Map<number, boolean>()

  private buildIceBurstPools(): void {
    if (!this.library || this.iceInstances.length > 0) return
    const built: ShardPool[] = []
    for (const name of ICE_BREAK_VFX.meshes) {
      const geometry = this.library.peekMesh(name)
      if (!geometry) return
      // ❓ Drawn in the ice shell's own ToonMaterial (`ICE_SHARD_MATERIAL.drawnAs`,
      // user's call) so a shard looks like the shell it came off. The pool's
      // flat colour is `Ice 1.mat`'s `_Color` (🔍), only reached if the
      // material override is ever dropped; the per-shard push is
      // `shardDrawColour`'s call (❓ `usesParticleColour`).
      const [r, g, b] = ICE_SHARD_MATERIAL.colour
      const pool = this.track(
        new ShardPool(geometry, new Color().setRGB(r, g, b, 'srgb'), 1, ICE_BURST_POOL, `iceShards-${name}`, {
          opaque: ICE_SHARD_MATERIAL.opaque,
          material: this.library.material(ICE_SHARD_MATERIAL.drawnAs),
        }),
      )
      this.object.add(pool.mesh)
      built.push(pool)
    }
    this.iceInstances = built
  }

  /** Fire a burst off this bus's body. Silent when the art is not loaded. */
  private fireIceBurst(bus: Bus): void {
    this.buildIceBurstPools()
    if (this.iceInstances.length === 0) return
    const node = this.busNodes.get(bus.id)
    const geometry = this.library?.peekMesh(BUS_SINGLE_MESH[bus.size])
    if (!node || !geometry) return
    node.updateMatrixWorld(true)
    // Every 16th triangle: the body has thousands and the burst has 60, so a
    // full list is pure cost.
    const triangles = emitterTriangles(geometry, node.matrixWorld, 16)
    const room = ICE_BURST_POOL - this.iceShards.length
    if (room <= 0) return
    // 📐 The system's own origin — `ice_vfx` at local (0, 4.36, 0) under the
    // bus root (prefab `:17638`) — is what `VelocityModule.radial` pushes away
    // from. Unity's local y is the port's y; the flip is on z (PORTING §1).
    const origin = new Vector3(0, ICE_VFX_LOCAL_Y, 0).applyMatrix4(node.matrixWorld)
    this.iceShards.push(
      ...emitIceBurst(triangles, bus.id, Math.min(ICE_BREAK_VFX.burstCount, room), origin),
    )
  }

  private syncIceBurst(dt: number): void {
    if (this.iceShards.length > 0) advanceIceBurst(this.iceShards, dt)
    if (this.iceInstances.length === 0) return

    for (const pool of this.iceInstances) pool.begin()
    for (const shard of this.iceShards) {
      const pool = this.iceInstances[shard.mesh]
      if (!pool) continue
      // `size` is `startSize × SizeModule.curve(age)`; `rotation` is
      // `startRotation` about world Z (`m_RenderAlignment: 1` World) and never
      // changes, since `RotationModule` is off.
      _shardMatrix.makeRotationZ(shard.rotation)
      _shardMatrix.scale(_shardScale.setScalar(shard.size))
      _shardMatrix.setPosition(shard.position)
      const draw = shardDrawColour(shard)
      pool.push(_shardMatrix, draw.tint, draw.alpha)
    }
    for (const pool of this.iceInstances) pool.end()
  }

  // -------------------------------------------------------------------------
  // The ice-countdown puff (IC-5c-iii)
  // -------------------------------------------------------------------------

  /**
   * `Bus.DecreaseIceCount` plays `iceCountdownParticle` **with children**, which
   * is four systems at once — see `ICE_COUNT_VFX`. Unlike the break burst these
   * are textured sprites, so each system gets its own quad pool and its own
   * material, and the two `_ADD` ones blend additively.
   *
   * The trigger is a **drop** in `iceCount`, not the shell going away: that is
   * `fireIceBurst`'s edge, and the two fire on different frames — the last chip
   * plays this, then `BreakIce` plays the other.
   */
  private countPuffs: Puff[] = []
  private countInstances: ParticlePool[] = []
  /** Last frame's ice count per bus, for the edge. */
  private iceCountWas = new Map<number, number>()
  /**
   * The camera sprites turn to face, when the caller has one.
   *
   * Set by whoever owns the camera — `main.ts` in the browser, the render
   * check in `renderPreview`. Left null a headless test still gets positions,
   * just not billboarding.
   */
  billboardCamera: Object3D | null = null

  private buildCountPuffPools(): void {
    if (!this.library || this.countInstances.length > 0) return
    const built: ParticlePool[] = []
    for (const spec of PUFF_SYSTEMS) {
      const texture = this.library.texture(PUFF_TEXTURE[spec.material])
      // All four or none, as `buildBusParticlePools`: a half-built set would
      // draw some of a puff and silently drop the rest.
      if (!texture) return
      // `ParticlePool` rather than a bare `InstancedMesh`: the `ColorModule` on
      // all four needs a per-instance tint **and** alpha (FRZ-7), and folding
      // the alpha into the colour is the mistake that pool's header documents.
      // `_ADD` in the material's own name is Unity's convention for the
      // additive variant, and it is what `glow2_ADD` is: a glow adds light
      // rather than painting over what is behind it.
      const pool = this.track(
        new ParticlePool(texture, spec.material.endsWith('_ADD'), COUNT_PUFF_POOL, `icePuff-${spec.node}`),
      )
      this.object.add(pool.mesh)
      built.push(pool)
    }
    this.countInstances = built
  }

  /** Fire the puff at the bus's roof. Silent when the art is not loaded. */
  private fireCountPuff(bus: Bus): void {
    this.buildCountPuffPools()
    if (this.countInstances.length === 0) return
    const node = this.busNodes.get(bus.id)
    if (!node) return
    node.updateMatrixWorld(true)
    const room = COUNT_PUFF_POOL - this.countPuffs.length
    if (room <= 0) return
    // The emitter places the box shapes in the bus's own frame; an iced bus is
    // parked, so the root pose is the pose (no bend to ride, unlike the FX
    // anchors).
    this.countPuffs.push(...emitIceCountPuff(node.matrixWorld, bus.size, bus.id).slice(0, room))
  }

  private syncCountPuff(dt: number, camera?: Object3D): void {
    if (this.countPuffs.length > 0) advanceIceCountPuff(this.countPuffs, dt)
    if (this.countInstances.length === 0) return

    for (const pool of this.countInstances) pool.begin()
    for (const puff of this.countPuffs) {
      const pool = this.countInstances[puff.system]
      if (!pool) continue
      // `size` is `startSize × SizeModule.curve(age)`; `alpha` and `tint` are
      // the `ColorModule` gradient at the same age — all kept current by
      // `advanceIceCountPuff`. Nothing to draw once either reaches zero.
      if (puff.size <= 0 || puff.alpha <= 0) continue
      const spec = PUFF_SYSTEMS[puff.system]
      // Billboards: a sprite faces the camera, so the quad takes the camera's
      // rotation rather than any of its own; then the roll about its facing
      // axis (`startRotation` + `RotationModule`), then the renderer's
      // `m_Pivot` (FRZ-9) — all in `puffSpriteMatrix`. Without a camera — the
      // render check has one, a unit test does not — they lie flat, which is
      // still a position worth checking.
      puffSpriteMatrix(puff, spec, camera ? camera.quaternion : null, _puffMatrix)
      _puffColour.setRGB(puff.tint[0], puff.tint[1], puff.tint[2])
      // `UVModule`: `Smoke` draws one cell of its 2×2 sheet, the others the map.
      pool.push(_puffMatrix, _puffColour, puff.alpha, frameUv(spec, puff.frame))
    }
    for (const pool of this.countInstances) pool.end()
  }

  // -------------------------------------------------------------------------
  // The bus's own particle systems, and the crash burst (FX-5)
  // -------------------------------------------------------------------------

  /**
   * 📐 The exhaust puff, the road trail, the depart glow and the wind lines.
   *
   * One `InstancedMesh` of camera-facing quads per system, and one
   * `EmitterState` per bus per system. The simulation is `busParticles.ts`;
   * this places the emitter, decides whether it is playing, and draws.
   *
   * **Where the emitter is** comes from `BUS_FX_ANCHORS` — the composed local
   * transform of the node in the prefab, per size. The exhaust really is a
   * tailpipe off to one side, not a plume from the middle.
   *
   * ⚠️ `departGlow` is drawn **tinted with the bus's own colour**, because
   * `PlayDepartEffects` writes `main.startColor = _color.GetEditorColor()`
   * before playing it. That makes it the one system whose material cannot be
   * shared between buses, so the colour is carried per particle and applied
   * through `instanceColor`.
   */
  private busParticles: Particle[][] = BUS_SYSTEMS.map(() => [])
  private busParticleInstances: ParticlePool[] = []
  /** Per bus, per system, indexed the same way as `BUS_SYSTEMS`. */
  private busEmitters = new Map<number, EmitterState[]>()

  private buildBusParticlePools(): void {
    if (!this.library || this.busParticleInstances.length > 0) return
    const built: ParticlePool[] = []
    for (const system of BUS_SYSTEMS) {
      const texture = this.library.texture(PUFF_TEXTURE[system.spec.material])
      // All four or none: a half-built set would draw some of a bus's exhaust
      // and silently drop the rest, which reads as a rendering bug rather than
      // a missing asset.
      if (!texture) return
      const pool = this.track(
        new ParticlePool(
          texture,
          system.spec.material.endsWith('_ADD'),
          BUS_PARTICLE_POOL,
          `busFx-${system.key}`,
        ),
      )
      this.object.add(pool.mesh)
      built.push(pool)
    }
    this.busParticleInstances = built
  }

  /**
   * Advance and emit every bus system for a frame.
   *
   * The emitter is placed in **Unity space** and converted once, like everything
   * else the view draws — the anchors are prefab-local offsets, so they are
   * rotated by the bus's own orientation before being added to its position.
   */
  private syncBusParticles(dt: number, camera?: Object3D): void {
    this.buildBusParticlePools()

    const live = new Set<number>()
    for (const bus of this.session.buses) {
      live.add(bus.id)
      let states = this.busEmitters.get(bus.id)
      if (!states) {
        states = BUS_SYSTEMS.map(() => newEmitter())
        this.busEmitters.set(bus.id, states)
      }

      // 🔴 The bus's pose comes from the **session**, not from the drawn node.
      //
      // `BusFleet.poseBent` zeroes `visual.node` and bakes the world position
      // into the vertices, because a bus on the road is a regenerated mesh
      // rather than a rigid object moved about. So reading `matrixWorld` here
      // gave identity for every bus on the road, and the whole exhaust was
      // emitted next to the world origin instead of behind the bus — which is
      // why the smoke looked "hidden behind something". It was not hidden; it
      // was somewhere else. Parked buses were fine, because for those the node
      // really does carry the pose, so the bug was invisible in the lot.
      //
      // `bus.position` / `bus.rotation` are kept for every bus by
      // `GameSession` (see `placeBus`), already in the view's space.
      _fxOrigin.copy(bus.position)

      for (let i = 0; i < BUS_SYSTEMS.length; i++) {
        // Reset per system: the bent path overwrites it with the cap's frame.
        _fxQuat.copy(bus.rotation)
        const system = BUS_SYSTEMS[i]
        const state = states[i]
        const playing = system.on(bus)
        if (playing && !state.playing) play(state)
        else if (!playing && state.playing) stopEmitting(state)

        // Where the emitter is.
        //
        // 🔴 A bus on the road is **bent**, and a rigid offset from the root
        // does not follow the bend: on a curve it lands on the bodywork instead
        // of behind the tail, which is what "the smoke appears on the side of
        // the bus when it turns" looks like.
        //
        // 🔍 The original never has this: the smoke hangs off `Parts/Tail` and
        // the glow off `Parts/Head`, and `UpdateCapBodies` puts those two caps
        // **on the curve**. So ask the follower for the cap's own pose and
        // offset from there — see `BUS_FX_CAPS`. A parked bus has no follower
        // and is not bent, so it keeps the rigid path.
        const anchor = BUS_FX_ANCHORS[bus.size][system.key]
        const follower = this.session.followerOf(bus)
        const along = follower
          ? (this.fleet?.alongForUnityZ(bus, follower, anchor[2]) ?? null)
          : null
        if (follower && along !== null) {
          // 🔴 `alongForUnityZ` is the **same map the mesh uses**, not an
          // approximation of it. An earlier attempt placed the exhaust at
          // `bodyPose(grownBodyLength)` on the assumption that is the tail — it
          // is the end of the **middle**, with the tail cap a further
          // `capBackRoom` behind. The smoke therefore came out a whole cap too
          // far forward, which on a curve puts it on the bodywork, and worse the
          // longer the bus because the cap is longer. That is the "smoke comes
          // out of the side of a long bus" this fixes.
          follower.bodyPose(along, _bodyPose, true)
          convertPosition(_bodyPose.position, _fxAnchor)
          convertPosition(_bodyPose.tangent, _capForward)
          _capForward.y = 0
          if (_capForward.lengthSq() < 1e-9) _capForward.set(0, 0, -1)
          _capForward.normalize()
          _capRight.crossVectors(_capForward, UP).normalize()

          // `along` already carries the position down the bus, so only the
          // sideways and vertical parts of the anchor are left.
          _fxAnchor.addScaledVector(_capRight, anchor[0])
          _fxAnchor.y += anchor[1]

          // The cone still needs a basis; the cap's own frame is the honest one
          // on a curve, where the root's rotation lags the tail.
          _fxQuat.setFromRotationMatrix(
            _capBasis.makeBasis(_capRight, UP, _capForward.clone().negate()),
          )
        } else {
          // Unity +Z is forward; the view flips Z, which is what convertPosition
          // does for every other position it draws.
          _fxAnchor.set(anchor[0], anchor[1], -anchor[2])
          _fxAnchor.applyQuaternion(_fxQuat).add(_fxOrigin)
        }

        // ❗ `BUS_FX_BOOST` is tuning, not the prefab — see its comment. The
        // prefab's own rate is `system.spec.rateOverTime`; multiplying here
        // keeps the spec pristine and the deviation in one named place.
        const boost = BUS_FX_BOOST[system.key]
        const due = emissionDue(
          state,
          dt,
          _fxAnchor,
          system.spec.rateOverTime * boost.rate,
          system.spec.rateOverDistance * boost.rate,
          system.spec.simulationSpeed,
        )
        if (due <= 0) continue

        const pool = this.busParticles[i]
        const room = BUS_PARTICLE_POOL - pool.length
        if (room <= 0) continue
        const born = emitParticles(
          scaledSpec(system.spec as ParticleSpec, boost.size),
          Math.min(due, room),
          _fxAnchor,
          _fxQuat,
          // Bus id and system index together, so two systems on one bus do not
          // draw the same random numbers and come out in lockstep.
          bus.id * 977 + i * 31,
          state.emitted,
          i,
        )
        state.emitted += born.length
        // Where the exhaust came out, so a puff left behind in a tunnel is
        // dropped with the bodywork that made it. `along` is non-null only
        // while the bus is laid out on the road.
        if (follower && along !== null && follower.ridesMainSpline) {
          const born_at = follower.roadHeadDistance - along
          for (const particle of born) particle.roadDistance = born_at
        }
        pool.push(...born)

        // `PlayDepartEffects` tints this one to the bus's colour, per particle
        // — see `Particle.tint` for why not on the material.
        if (system.key === 'departGlow') {
          const tint = colorOf(bus.visibleColor).getHex()
          for (const particle of born) particle.tint = tint
        }
      }
    }

    // A bus that has left takes its emitters with it; the particles it already
    // put in the air outlive it, which is what `Destroy` does to a system whose
    // children are world-space.
    for (const id of [...this.busEmitters.keys()]) if (!live.has(id)) this.busEmitters.delete(id)

    for (let i = 0; i < BUS_SYSTEMS.length; i++) {
      const spec = BUS_SYSTEMS[i].spec
      advanceParticles(this.busParticles[i], dt * spec.simulationSpeed, spec.gravityModifier, spec)
    }

    this.drawBusParticles(camera)
  }

  private drawBusParticles(camera?: Object3D): void {
    if (this.busParticleInstances.length === 0) return
    for (let i = 0; i < BUS_SYSTEMS.length; i++) {
      const pool = this.busParticleInstances[i]
      const spec = BUS_SYSTEMS[i].spec as ParticleSpec
      const particles = this.busParticles[i]
      pool.begin()
      for (const p of particles) {
        // `alphaOf` fades the sprite and `sizeOf` scales it — the two curves the
        // prefab switches on. Alpha goes to the pool as its own attribute, not
        // folded into the tint; see `ParticlePool` for why that distinction
        // cost a rewrite.
        const alpha = Math.min(1, alphaOf(spec, p) * BUS_FX_BOOST[BUS_SYSTEMS[i].key].alpha)
        const size = sizeOf(spec, p)
        if (size <= 0 || alpha <= 0) continue
        // Out past the tarmac the bus is not drawn, so its exhaust is not either.
        if (p.roadDistance !== undefined && this.outsideRoad(p.roadDistance)) continue
        _fxMatrix.makeScale(size, size, size)
        if (camera) _fxMatrix.multiply(_fxBillboard.makeRotationFromQuaternion(camera.quaternion))
        // `startRotation` — a spin about the sprite's own facing axis, which for
        // a billboard is the camera's forward. Applied after the billboard turn
        // so it rolls the quad in view space rather than in the world.
        if (p.spin !== 0) _fxMatrix.multiply(_fxSpin.makeRotationZ(p.spin))
        _fxMatrix.setPosition(p.position)

        // `startColor` is a grey on the two smoke systems; `PlayDepartEffects`
        // replaces it outright on the glow.
        if (p.tint !== undefined) _fxColour.setHex(p.tint)
        else _fxColour.setScalar(p.grey)
        if (!pool.push(_fxMatrix, _fxColour, alpha, frameUv(spec, p.frame))) break
      }
      pool.end()
    }
  }

  /**
   * 🔍 `BusCrashVFXManager` — the spark burst on a bus-to-bus bump.
   *
   * `OnBusCrashed` moves **one** pooled instance to `CrashPosition + up *
   * yOffset` and calls `Play(withChildren: true)`, which fires all three
   * systems at once. There is no timer and no second instance: two collisions
   * close together restart the same burst rather than stacking, because the
   * manager only ever instantiated one in `Awake`.
   *
   * ⚠️ The port keeps the particles from an earlier burst alive rather than
   * clearing them, which is what `Play` on an already-playing non-looping
   * system does — `Stop` is only called from `OnLevelEnded`.
   */
  private crashParticles: Particle[] = []
  private crashInstances: ParticlePool[] = []

  private buildCrashPools(): void {
    if (!this.library || this.crashInstances.length > 0) return
    const built: ParticlePool[] = []
    for (const spec of BUS_CRASH_VFX.systems) {
      const texture = this.library.texture(PUFF_TEXTURE[spec.material])
      if (!texture) return
      const pool = this.track(
        new ParticlePool(
          texture,
          spec.material.endsWith('_ADD'),
          CRASH_PARTICLE_POOL,
          `crashFx-${spec.node}`,
        ),
      )
      this.object.add(pool.mesh)
      built.push(pool)
    }
    this.crashInstances = built
  }
  /** `OnBusCrashed`: place the instance and `Play(withChildren: true)`. */
  fireCrashBurst(position: Vector3): void {
    this.buildCrashPools()
    // `e.CrashPosition + Vector3.up * yOffset`.
    //
    // 🔴 **No `convertPosition` here.** This line used to read
    // `convertPosition(position, _fxOrigin)` under the comment "the position
    // arrives in Unity space from the rules, like everything else they hand
    // over" — and that premise is false. `Bus`'s constructor already flips the
    // level's Unity z (`bus.ts`:308, `convertPosition(spec.position, …)`), so
    // every position the rules hand out is **view** space: `node.position.copy(
    // bus.position)` and `poseRigid`'s `visual.node.position.copy(bus.position)`
    // both copy it straight through, and `this.object` carries no transform of
    // its own. `scanForward` builds the crash point out of `bus.position` too,
    // so a second flip here mirrored the burst across the z = 0 plane.
    //
    // Measured on level 12 (bus #3 rams #2, both parked at z = −6): the sparks
    // projected to screen (371, 932) while the two buses sat at (291, 764) and
    // (449, 764) — 168 px away, the whole width of the lot, on the wrong side of
    // the road. See `showCantMove`, which had the same flip for the same reason.
    _fxOrigin.copy(position)
    _fxOrigin.y += BUS_CRASH_VFX.yOffset

    // No rotation is ever set on the instance, so the burst is axis-aligned in
    // world space however the two buses were pointing.
    this.crashSeed++
    for (let i = 0; i < BUS_CRASH_VFX.systems.length; i++) {
      const spec = BUS_CRASH_VFX.systems[i] as ParticleSpec & { burstCount: number }
      const room = CRASH_PARTICLE_POOL - this.crashParticles.length
      if (room <= 0) return
      // ❗ Tuning again — `BUS_CRASH_BOOST`.
      const count = Math.round(spec.burstCount * BUS_CRASH_BOOST.rate)
      this.crashParticles.push(
        ...emitParticles(
          scaledSpec(spec, BUS_CRASH_BOOST.size),
          Math.min(count, room),
          _fxOrigin,
          _identityQuat,
          this.crashSeed * 131 + i * 17,
          0,
          i,
        ),
      )
    }
  }

  private crashSeed = 0

  /**
   * The two rotations a bus carries while it is still in the lot, applied on
   * top of whatever pose already sits on `target`.
   *
   * 🔍 `ShakeObject`'s `DOPunchRotation((1,3,3), 0.2f, 10, 1f)` and
   * `MoveBoxToTarget`/`MoveAfterCollision`'s
   * `DOLocalRotate(euler + Vector3.left|right * 7f)` both drive the **same**
   * transform in the original — `BusMovementHandler._targetTransform` (0x78) —
   * so both are post-multiplied here, about the bus's own axes rather than the
   * world's.
   *
   * Called for every node that draws a lot bus: the marker `node` and the
   * shipped art, which are two different objects.
   */
  private applyLotPose(target: Object3D, bus: Bus): void {
    if (bus.shakeEuler.lengthSq() > 0) {
      _shakeEuler.set(
        bus.shakeEuler.x * DEG_TO_RAD,
        bus.shakeEuler.y * DEG_TO_RAD,
        bus.shakeEuler.z * DEG_TO_RAD,
        'YXZ',
      )
      target.quaternion.multiply(_shakeRot.setFromEuler(_shakeEuler))
    }

    if (bus.leanEuler.x !== 0) {
      _shakeEuler.set(bus.leanEuler.x * DEG_TO_RAD, 0, 0, 'YXZ')
      target.quaternion.multiply(_shakeRot.setFromEuler(_shakeEuler))
    }
  }

  private syncCrashBurst(dt: number, camera?: Object3D): void {
    if (this.crashParticles.length > 0) {
      // Each system has its own gravity — the sparks fall at 1.4x, the shards
      // and the glow not at all — so they are stepped per system rather than
      // in one pass.
      for (let i = 0; i < BUS_CRASH_VFX.systems.length; i++) {
        const mine = this.crashParticles.filter((p) => p.system === i)
        advanceParticles(mine, dt, BUS_CRASH_VFX.systems[i].gravityModifier, BUS_CRASH_VFX.systems[i])
      }
      this.crashParticles = this.crashParticles.filter((p) => p.age < p.lifetime)
    }
    if (this.crashInstances.length === 0) return

    for (const pool of this.crashInstances) pool.begin()
    for (const p of this.crashParticles) {
      const pool = this.crashInstances[p.system]
      if (!pool) continue
      const spec = BUS_CRASH_VFX.systems[p.system] as ParticleSpec
      const alpha = alphaOf(spec, p)
      const size = sizeOf(spec, p)
      if (size <= 0 || alpha <= 0) continue
      if (p.roadDistance !== undefined && this.outsideRoad(p.roadDistance)) continue
      _fxMatrix.makeScale(size, size, size)
      if (camera) _fxMatrix.multiply(_fxBillboard.makeRotationFromQuaternion(camera.quaternion))
      if (p.spin !== 0) _fxMatrix.multiply(_fxSpin.makeRotationZ(p.spin))
      _fxMatrix.setPosition(p.position)
      // All three crash systems ship a constant white `startColor`, so the tint
      // is white and only the alpha curve varies.
      pool.push(_fxMatrix, WHITE, alpha, frameUv(spec, p.frame))
    }
    for (const pool of this.crashInstances) pool.end()
  }

  // -------------------------------------------------------------------------
  // Skid marks (FX-7)
  // -------------------------------------------------------------------------

  /**
   * 📐 The two rear `TrailRenderer`s, as one ribbon mesh per level.
   *
   * A ribbon is not instanced geometry — each is a strip of quads whose corners
   * move every frame — so this is a single `BufferGeometry` rewritten in place,
   * sized for the worst case of every bus in the lot skidding at once.
   *
   * ⚠️ Only ever seen inside the lot. `EnterSplineRideMode` destroys the
   * trails when a bus is admitted to the road, so `DepartAndExit`'s
   * `ToggleSkidEmitting(true)` switches nothing — see `BUS_SKID`.
   */
  private skidRibbons = new Map<number, SkidRibbon[]>()
  private skidMesh: Mesh | null = null
  private skidPositions: Float32Array | null = null
  private skidAlphas: Float32Array | null = null

  private buildSkidMesh(): void {
    if (this.skidMesh) return
    const geometry = this.track(new BufferGeometry())
    this.skidPositions = new Float32Array(SKID_QUADS * 6 * 3)
    this.skidAlphas = new Float32Array(SKID_QUADS * 6)
    geometry.setAttribute('position', new BufferAttribute(this.skidPositions, 3))
    geometry.setAttribute('aAlpha', new BufferAttribute(this.skidAlphas, 1))
    geometry.setDrawRange(0, 0)
    const [r, g, b] = BUS_SKID.colour
    const material = this.track(
      new ShaderMaterial({
        uniforms: { tint: { value: new Color().setRGB(r, g, b, 'srgb') } },
        transparent: true,
        depthWrite: false,
        vertexShader:
          'attribute float aAlpha; varying float vA;' +
          'void main() { vA = aAlpha;' +
          ' gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
        fragmentShader:
          'uniform vec3 tint; varying float vA;' +
          'void main() { if (vA <= 0.002) discard; gl_FragColor = vec4(tint, vA); }',
      }),
    )
    const mesh = new Mesh(geometry, material)
    mesh.name = 'skidMarks'
    mesh.frustumCulled = false
    // Under the buses and over the road: the marks are on the tarmac.
    mesh.renderOrder = -1
    this.object.add(mesh)
    this.skidMesh = mesh
  }

  private syncSkids(dt: number): void {
    this.buildSkidMesh()
    const positions = this.skidPositions
    const alphas = this.skidAlphas
    if (!positions || !alphas || !this.skidMesh) return

    let quads = 0
    const live = new Set<number>()

    for (const bus of this.session.buses) {
      live.add(bus.id)
      const anchors = BUS_SKID.anchors[bus.size]
      let ribbons = this.skidRibbons.get(bus.id)
      if (!ribbons) {
        ribbons = anchors.map(() => newRibbon())
        this.skidRibbons.set(bus.id, ribbons)
      }

      // From the session's pose, for the same reason the particles are — see
      // `syncBusParticles`. Skids only ever run in the lot, where the node
      // would have been right, but reading two different sources for the same
      // question is how the next reader inherits the bug.
      for (let i = 0; i < anchors.length; i++) {
        const ribbon = ribbons[i]
        ribbon.emitting = bus.skidding
        const a = anchors[i]
        // Unity +Z forward; the view flips Z, like every other position.
        _skidAt.set(a[0], a[1], -a[2]).applyQuaternion(bus.rotation).add(bus.position)
        advanceSkid(ribbon, _skidAt, dt)
        quads = this.writeSkid(ribbon, positions, alphas, quads)
      }
    }

    for (const id of [...this.skidRibbons.keys()]) if (!live.has(id)) this.skidRibbons.delete(id)

    this.skidMesh.geometry.setDrawRange(0, quads * 6)
    ;(this.skidMesh.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true
    ;(this.skidMesh.geometry.getAttribute('aAlpha') as BufferAttribute).needsUpdate = true
    this.skidMesh.visible = quads > 0
  }

  /** One ribbon into the shared buffers. Returns the new quad count. */
  private writeSkid(
    ribbon: SkidRibbon,
    positions: Float32Array,
    alphas: Float32Array,
    quads: number,
  ): number {
    const segments = skidSegments(ribbon)
    for (let i = 0; i + 1 < segments.length; i++) {
      if (quads >= SKID_QUADS) return quads
      const a = segments[i]
      const b = segments[i + 1]
      // The ribbon lies flat on the road, so its width runs along the horizontal
      // normal of the direction of travel.
      _skidDir.subVectors(b.position, a.position)
      _skidDir.y = 0
      if (_skidDir.lengthSq() < 1e-10) continue
      _skidDir.normalize()
      _skidSide.set(-_skidDir.z, 0, _skidDir.x)

      const base = quads * 6 * 3
      const abase = quads * 6
      const corner = (n: number, p: Vector3, half: number, sign: number, alpha: number): void => {
        positions[base + n * 3] = p.x + _skidSide.x * half * sign
        positions[base + n * 3 + 1] = p.y
        positions[base + n * 3 + 2] = p.z + _skidSide.z * half * sign
        alphas[abase + n] = alpha
      }
      corner(0, a.position, a.halfWidth, -1, a.alpha)
      corner(1, a.position, a.halfWidth, 1, a.alpha)
      corner(2, b.position, b.halfWidth, 1, b.alpha)
      corner(3, a.position, a.halfWidth, -1, a.alpha)
      corner(4, b.position, b.halfWidth, 1, b.alpha)
      corner(5, b.position, b.halfWidth, -1, b.alpha)
      quads++
    }
    return quads
  }

  readonly pickTargets: Object3D[] = []
  private readonly pickToBus = new Map<Object3D, Bus>()
  private readonly fleet: BusFleet | null
  /** Every passenger in the level — queues, crowds, boarders and riders. */
  private readonly people: CharacterView | null

  constructor(
    private session: GameSession,
    private readonly library: AssetLibrary | null = null,
    /** Shared, not copied — the HUD sliders mutate it live. */
    busBodyTuning?: BusBodyTuning,
  ) {
    this.object.name = 'session'
    this.fleet = library ? new BusFleet(library) : null
    if (this.fleet && busBodyTuning) this.fleet.bodyTuning = busBodyTuning
    this.people = library ? new CharacterView(library) : null
    if (this.people) {
      this.object.add(this.people.object)
      this.disposables.push(this.people)
    }
    this.buildRoadWindow()
    for (const bus of session.buses) this.buildBus(bus)

    // `BusController.cantMoveIndicator` is one node on the controller, not one
    // per bus — it is moved onto whichever bus was refused.
    this.cantMove = new CantMoveIndicator(this.object)
    this.disposables.push(this.cantMove)
  }

  /**
   * `BusController.OnBoxClicked`'s inlined `AnimateCantMoveIndicator`. Called by
   * whoever owns the session's events; see `SessionEvents.onCantMove`.
   */
  showCantMove(position: Vector3, spaceMaxed: boolean): void {
    // 🔴 No flip — same reason as `fireCrashBurst`. The comment here used to
    // read "the rules work in Unity space, like every other position they hand
    // over"; they do not, and `bus.position` is what `node.position` copies
    // verbatim two hundred lines down. The label was landing mirrored across
    // z = 0, over whatever happened to be parked opposite the refused bus.
    _fxOrigin.copy(position)
    this.cantMove.play(_fxOrigin, spaceMaxed)
  }

  /**
   * The stretch of the loop that has tarmac on it, once per level.
   *
   * One `Vector2` shared by every cutting material — see `RoadWindow` for why
   * this is an arc-distance window rather than the pair of world-space boxes it
   * replaced, and for the two bugs those boxes cost.
   */
  private buildRoadWindow(): void {
    const road = this.session.road
    const spline = road.spline
    this.roadWindow.set(
      spline.distanceAt(road.originalStartPercent),
      spline.distanceAt(road.originalEndPercent),
    )
    // A level constant, written once here and never touched again.
    ;(RoadWindow.value as Vector2).copy(this.roadWindow)
  }

  /**
   * Is a point this far along the road out past either end of the tarmac?
   *
   * The same test the shader runs, for the two things the shader cannot reach:
   * riders, which come from one instanced batch shared across every bus, and
   * exhaust puffs, which are loose in world space.
   *
   * `pad` shrinks the window at both ends. The shader cuts a bus per-fragment,
   * so it needs no slack; a rider is dropped **whole or not at all**, by the one
   * distance under their feet, and a rider is not a point — they are 1.24 *
   * `queueCharacterScale` across and stand 2.33 * scale tall on a roof that is
   * already 2.8 up. `ConveyorClose` is 4.01 tall, so a rider left standing a few
   * centimetres inside the mouth has their head over the cover's roof, which is
   * what you see: two heads sitting on the tunnel. Dropping them half a body
   * early puts the whole silhouette behind the cover before it can show.
   *
   * ❓ The original does not need this: `Bus.UpdateInvisibility` hides the bus
   * **and everything parented to it** — riders included — as one object, so no
   * rider is ever drawn beside a hidden bus. The port draws riders from a
   * separate instanced batch and so has to reproduce that by hand. The pad is
   * therefore a port-side device with no counterpart to read; its size is the
   * rider's own half-width, which is the smallest value that does the job.
   */
  private outsideRoad(distance: number, pad = 0): boolean {
    return distance < this.roadWindow.x + pad || distance > this.roadWindow.y - pad
  }

  /**
   * Does this bus draw its body along the road, and so read `aRoadDistance`?
   *
   * That is the whole question now, and it is a yes or a no — see `RoadWindow`
   * for why it used to be four-valued and why asking `UpdateInvisibility` for
   * the answer was wrong. Where the cut falls is decided per vertex, by the
   * vertex, so nothing here has to know how far into a tunnel the bus has got.
   *
   * A bus on its merge curve or its run-off is not on the road at all: its
   * distances are measured along a throwaway spline, so `aRoadDistance` would
   * be a number from the wrong ruler. The original agrees for its own reason —
   * `UpdateInvisibility` returns without hiding whenever `IsDeparting` is set.
   */
  private roadCutFor(bus: Bus): ClipMask {
    const follower = this.session.followerOf(bus)
    if (!follower) return 0
    // `PlayFailCrash` opens with `this.enabled = false`, so `UpdateInvisibility`
    // stops running the moment a bus starts to roll — it is no longer on the
    // road and the arc question has no answer for it. Leaving the cut armed
    // sliced the rolled body against a window it had swung out of.
    if (this.tipOf(bus.id)) return 0
    return follower.ridesMainSpline ? ROAD_CUT : 0
  }

  /** The roll this bus is in the middle of, if any. */
  private tipOf(busId: number): BusTip | null {
    for (const tip of this.session.busTips) {
      if (tip.busId === busId) return tip
    }
    return null
  }

  /**
   * `Transform.RotateAround(pivot, axis, angle)`, as a node transform.
   *
   * `poseBent` leaves the body's vertices in **world** space under an identity
   * node, so rotating the drawn bus about a world pivot is exactly a rotation
   * of that node: a point `p` must land at `pivot + R * (p - pivot)`, which is
   * `R` with a translation of `pivot - R * pivot`. Same arithmetic for the
   * parked-bus path, where the node is a real transform, because `poseRigid`
   * writes the node's own position and this composes on top of it.
   */
  private applyTip(node: Object3D, tip: BusTip): void {
    // The axis is in Unity space, like everything the rules produce — and the
    // **angle flips sign** on the way across. `convertPosition` is `(x, y, -z)`,
    // a reflection, so conjugating a Unity rotation by it gives a rotation about
    // `convertPosition(axis)` by `-angle`. That is the same rule
    // `convertQuaternion` already encodes: for `q = (a·sin(t/2), cos(t/2))`,
    // `(-qx, -qy, qz, qw)` is exactly `axisAngle((ax, ay, -az), -t)`.
    //
    // Dropping the minus used to be invisible because `_crashDir` had the axis
    // backwards as well, and `R(-a, +t) == R(a, -t)`. The two errors cancelled
    // in the rotation but **not** in the pivot, which `Cross(up, dir)` puts on
    // the wrong side — so the bus rolled about its far wheel and drove its roof
    // through the road. Fixing either one alone breaks the rotation that
    // currently works; they are a pair.
    //
    // `convertAxisAngle` rather than open-coding it, so the sign has one home
    // and a test can reach it without a renderer.
    convertAxisAngle(tip.axis, tip.angle, _tipRot)
    convertPosition(tip.pivot, _tipPivot)
    node.position.sub(_tipPivot).applyQuaternion(_tipRot).add(_tipPivot)
    node.quaternion.premultiply(_tipRot)
  }

  /**
   * Is there anything to cut here?
   *
   * The hit box and the lot arrow are plain three materials with no clip term,
   * and neither is ever on screen at a tunnel anyway.
   */
  private canClip(material: Material | Material[]): boolean {
    if (!this.library) return false
    return Array.isArray(material)
      ? material.some((m) => m instanceof ToonMaterial)
      : material instanceof ToonMaterial
  }

  /** The materials for one mask, built the first time that mask comes up. */
  private clipVariant(swap: BusMaterialSwap, mask: ClipMask): Material | Material[] {
    const cached = swap.variants[mask]
    if (cached) return cached
    const library = this.library!
    const plain = swap.variants[0]!
    const cut = (m: Material): Material =>
      m instanceof ToonMaterial ? library.clipped(m, mask) : m
    const built = Array.isArray(plain) ? plain.map(cut) : cut(plain)
    swap.variants[mask] = built
    return built
  }

  /**
   * Draw this bus cut at the mouths it is standing in, or whole — in the
   * colour pass and in the shadow pass alike (`roadCutShadow.ts`).
   */
  private setBusClipped(id: number, mask: ClipMask): void {
    const swaps = this.busSwaps.get(id)
    if (!swaps) return
    for (const swap of swaps) {
      swap.mesh.material = this.clipVariant(swap, mask)
      syncRoadCutShadow(swap.mesh)
    }
  }

  private track<T extends { dispose(): void }>(d: T): T {
    this.disposables.push(d)
    return d
  }

  // -------------------------------------------------------------------------

  /**
   * The direction flag on a parked bus's roof.
   *
   * `Arrow (1)` sits on every `ConveyorBox` prefab at (0, 4.28–4.36, 0) with a
   * quarter-turn about X, which lays a SpriteRenderer's XY quad flat on the
   * lid; a plane rotated the same way puts the sprite's +Y along the bus's
   * forward.
   *
   * The picture is `Arrow_0` — 512x512, a white arrow with a dark outline —
   * and not the outline-less 50x56 `Arrow`, which this drew until the outline
   * in the reference shot gave it away. Both the sprite and the size it draws
   * at are read off the prefab's SpriteRenderer; see `BUS_ARROW_SIZE` for why
   * the note claiming they were stripped was wrong. Sliced, so only the shaft
   * stretches between a Small bus and a Large one.
   */
  private buildArrow(bus: Bus): Object3D | null {
    const texture = this.library?.texture('Arrow_0')
    if (!texture) return null

    const slice = sliceArrow(BUS_ARROW_WIDTH, BUS_ARROW_SIZE[bus.size] ?? BUS_ARROW_SIZE[BoxSizeEnum.Medium])
    const geometry = this.track(new BufferGeometry())
    geometry.setAttribute('position', new Float32BufferAttribute(slice.positions, 3))
    geometry.setAttribute('uv', new Float32BufferAttribute(slice.uvs, 2))
    geometry.setIndex(slice.indices)
    geometry.computeVertexNormals()

    const arrow = new Mesh(
      geometry,
      this.track(
        new MeshBasicMaterial({
          map: texture,
          transparent: true,
          depthWrite: false,
          toneMapped: false,
        }),
      ),
    )
    // -90 degrees about X sends the plane's +Y to local -Z, which is forward
    // here — the mirror of Unity's local +Z.
    arrow.rotation.x = -Math.PI / 2
    arrow.position.y = BUS_ARROW_HEIGHT[bus.size] ?? 4.29
    arrow.renderOrder = 2
    arrow.name = 'arrow'
    return arrow
  }

  private buildBus(bus: Bus): void {
    const node = new Group()
    node.name = `bus-${bus.id}`
    const bodyCentreZ = -(BUS_CENTRE_Z[bus.size] ?? 0)

    // The shipped body, if the art is loaded. It carries its own transform —
    // parked it is rigid, on a curve it is bent — so it hangs off the scene
    // root rather than this node.
    const art = this.fleet?.build(bus) ?? null
    if (art) {
      this.object.add(art)
    } else {
      const body = new Mesh(
        this.track(new BoxGeometry(BUS_WIDTH, BUS_HEIGHT, bus.length)),
        this.track(new MeshLambertMaterial({ color: colorOf(bus.visibleColor) })),
      )
      body.position.set(0, BUS_HEIGHT / 2, bodyCentreZ)
      body.name = 'placeholderBody'
      node.add(body)

      // Nose cone, so which way the bus faces is unmistakable. Local -Z is
      // forward, matching Unity's local +Z through the mirror.
      const nose = new Mesh(
        this.track(new ConeGeometry(1.1, 1.8, 4)),
        this.track(new MeshBasicMaterial({ color: 0x14161c })),
      )
      nose.position.set(0, BUS_HEIGHT * 0.55, bodyCentreZ - bus.length / 2 - 0.9)
      nose.rotation.set(Math.PI / 2, 0, Math.PI / 4)
      node.add(nose)
    }

    if (bus.type === BoxTypeEnum.Ice) {
      // 🔍 **No shell mesh.** The ice is the body itself: `Bus.ApplyIceMaterialState`
      // (`__0x20f9eac.c`:84/:138/:192/:235/:284/:327) writes `iceMaterial` (0x78,
      // `IceBus.mat`) into element [0] of `sharedMaterials` on every renderer in
      // `meshRenderers`, `accommodationObjects` and the three `BusBodyMesh`, and
      // `BreakIce` (`:37`, `:65` `_type = 0` then the inlined `SwitchColor`) puts
      // the colour material back on the same slot. So the shipped art is built
      // with `IceBus` (`BusFleet.build` → `busBodyMaterials`) and rebuilt when
      // the type changes (`builtIced`, below in `sync`). A translucent box used
      // to stand here (FRZ-1/FRZ-8): a mesh the original has none of, over a
      // colour the original replaces.

      // 🔍 `Bus.iceCountText` (`dump.cs:709421`, field 0x80) — how many more
      // taps the shell takes. `DecreaseIceCount` writes
      // `iceCountText.text = _iceBreakCount.ToString()` after the decrement, and
      // `BreakIce` switches the node off at zero.
      //
      // The node is real and switched on in all three `ConveyorBox` prefabs: a
      // `RectTransform` at `m_AnchoredPosition (0, 4.7)`, `m_SizeDelta 20 x 5`,
      // `m_LocalEulerAnglesHint {x: 90}` — laid flat over the ice — parented to
      // the `Ice` node. Its `MeshRenderer` (u!23, not stripped) names
      // `MikadoBlack_Outline1.mat`, which is where `ICE_COUNT_STYLE` comes from.
      //
      // ❓ Only the typeface is substituted; see `worldText.ts`.
      const label = createWorldText({ width: 20, height: 5, style: ICE_COUNT_STYLE })
      if (label) {
        // 20 x 5 is already in bus units (no scale chain — see the constant),
        // but a TMP rect is a layout box, not the glyph size: the scale stands
        // in for the stripped `m_fontSize`. Laid flat with text-up toward the
        // nose, as the prefab's +90° about X puts it toward `Head` (+Z there,
        // −Z here); `worldText.ts` has the turn.
        label.object.scale.setScalar(ICE_COUNT_LABEL_SCALE)
        label.object.position.set(0, BUS_HEIGHT * 1.05 + 0.02, bodyCentreZ)
        node.add(label.object)
        this.busIceCount.set(bus.id, label)
      }
    }

    // Passengers stand *inside* the open box, three abreast — see
    // `BusSplineFollower.GetPassengerSlotPose`. They are positioned along the
    // road rather than on the bus node, so they hold station over a curve the
    // way the original's per-frame `UpdatePassengerPoses` does.
    void bodyCentreZ

    // ⚠️ **No held-at-stop marker.** A yellow cylinder used to stand over any
    // bus with `isHeldAtStop`, from the days when the boarding rules had no
    // visible effect and something had to say the hold was working. The
    // original draws nothing of the kind: `BusSplineFollower` contains no
    // `SetActive` call at all, and there is no hold/release method that touches
    // a GameObject. What the player is meant to see is the passengers walking
    // on, which the port now draws.

    const hit = new Mesh(
      this.track(new BoxGeometry(BUS_WIDTH, BUS_HEIGHT, bus.length)),
      this.track(new MeshBasicMaterial({ visible: false })),
    )
    hit.position.set(0, BUS_HEIGHT / 2, bodyCentreZ)
    node.add(hit)

    const arrow = this.buildArrow(bus)
    if (arrow) {
      node.add(arrow)
      this.busArrows.set(bus.id, arrow)
    }

    // Everything this bus draws, paired with the clipping twin of its material.
    //
    // Cutting the bus is a material swap, not a uniform written around the
    // draw: three uploads a ShaderMaterial's uniforms only when the material
    // changed since the last draw, and the opaque pass groups draws by
    // material, so a value written per bus reached only the first bus in each
    // group. `RoadClip` has the whole story.
    this.recollectMeshes(bus, node)

    this.object.add(node)
    this.busNodes.set(bus.id, node)
    this.builtColour.set(bus.id, bus.visibleColor)
    this.builtIced.set(bus.id, bus.type === BoxTypeEnum.Ice)
    const placeholder = node.getObjectByName('placeholderBody')
    if (placeholder) this.busBodies.set(bus.id, placeholder as Mesh)
    this.pickTargets.push(hit)
    this.pickToBus.set(hit, bus)
  }

  /** Every mesh a bus draws, marker box and shipped art alike. */
  busDrawables(id: number): readonly Mesh[] {
    return this.busMeshes.get(id) ?? []
  }

  /** Which tunnel mouths this bus is being cut by, as of the last `sync`. */
  busClipMask(id: number): ClipMask {
    return this.clipped.get(id) ?? 0
  }

  busFor(object: Object3D): Bus | null {
    let node: Object3D | null = object
    while (node) {
      const bus = this.pickToBus.get(node)
      if (bus) return bus
      node = node.parent
    }
    return null
  }

  // -------------------------------------------------------------------------

  readonly trace = new MergeTrace()
/**
   * Which tunnel mouths each bus is standing in, by id.
   *
   * Only the named mouths get a plane. The rider pass reads the same masks, so
   * a bus and the passengers on it are always cut by the same line — the
   * original moves the bus and every `Character` on it to the same layer in one
   * go, and a body sliced with its riders left floating would be worse than
   * either alone.
   */
  private readonly clipped = new Map<number, ClipMask>()

  /** Per bus, every mesh it draws and the materials it can draw with. */
  private readonly busSwaps = new Map<number, BusMaterialSwap[]>()

  /**
   * 🔍 `Bus._hiddenSequence` (0x148) while it is running — one per bus that is
   * mid-reveal. `elapsed` is the sequence's clock; `twins` are the
   * `Renderer.materials[0]` instances `PlayRevealMaterialTween` writes to, one
   * per material the bus is currently drawing with (plain or tunnel-cut).
   */
  private readonly revealing = new Map<number, RevealSequence>()

  /**
   * `SessionEvents.onBusRevealed` → `Bus.ExitHiddenState` (`__0x20f91bc.c`).
   *
   * What lands here is the material half — `PlayRevealMaterialTween`, inlined
   * at `:148-340`. The order the original imposes is kept by *where* the tween
   * is advanced, not by anything done here: `:135` `SwitchColor(_color, 0)` is
   * the rebuild in `sync` (`builtColour` re-keys on `visibleColor`, which is
   * real the moment `bus.type` left `Hidden`), and `advanceReveal` runs after
   * it, so the sequence's first frame writes range 0 / shadow black onto the
   * **real colour's** material, never onto `Bus_Gray` — HID-5, and the reason
   * HID-1 was refused.
   *
   * `:37` `_hiddenSequence.Kill()` first: a second call restarts the clock and
   * drops the instances the first one made. The 0.179 s squash-stretch that
   * `:86-133` inserts into the same sequence reads this clock too — see
   * `revealScaleY` and `BusFleet.setRevealScaleY` (HID-2).
   */
  busRevealed(bus: Bus): void {
    this.endReveal(bus.id)
    this.revealing.set(bus.id, { elapsed: 0, twins: new Map(), sources: new Map() })
  }

  /** Seconds into a bus's reveal sequence, or null when none is running. */
  revealProgress(bus: Bus): number | null {
    return this.revealing.get(bus.id)?.elapsed ?? null
  }

  /**
   * 🔍 `ExitHiddenState`:86-133 — the Y scale of `headPart` / `tailPart` /
   * `middleBody` this frame: the two `DOScaleY`s sit in the **same**
   * `_hiddenSequence` as the material tween, so they read the same clock.
   * 1 when no sequence is running (HID-2).
   */
  revealScaleY(bus: Bus): number {
    return revealScaleYAt(this.revealProgress(bus))
  }

  /**
   * One frame of `PlayRevealMaterialTween` on this bus's drawn meshes.
   *
   * Runs after `setBusClipped` and after the rebuild, so `mesh.material` is
   * whichever plain or road-cut material the bus should be drawing with this
   * frame; each is swapped for its per-bus instance and that instance gets the
   * frame's `_DissolveRange` / `_ShadowColor`. A road-cut material gets a cut
   * instance (`AssetLibrary.revealing` keeps `clip`), so a bus revealed at a
   * mouth is still cut there. When the sequence has run its 0.893 s the
   * instances are dropped and `setBusClipped` puts the plain material back —
   * range 1 and the authored shadow, which is what the instance ends at too.
   *
   * With no library there is nothing to draw, but the clock still runs so the
   * sequence's timing can be checked without a renderer.
   */
  private advanceReveal(bus: Bus, dt: number): void {
    const tween = this.revealing.get(bus.id)
    if (!tween) return
    if (tween.elapsed >= REVEAL_TWEEN_SECONDS) {
      this.endReveal(bus.id)
      return
    }
    const swaps = this.busSwaps.get(bus.id)
    if (this.library && swaps) {
      const instance = (m: Material): Material => {
        if (!(m instanceof ToonMaterial)) return m
        // A mesh that already holds this bus's instance (nothing reset it this
        // frame) keeps it; the source is what carries the tween's target.
        const source = tween.sources.get(m) ?? m
        if (!source.spec) return m
        let twin = tween.twins.get(source)
        if (!twin) {
          const made = this.library!.revealing(source)
          if (!made) return m
          twin = made
          tween.twins.set(source, twin)
          tween.sources.set(twin, source)
        }
        writeRevealTween(twin, source.spec, tween.elapsed)
        return twin
      }
      for (const swap of swaps) {
        const current = swap.mesh.material
        swap.mesh.material = Array.isArray(current) ? current.map(instance) : instance(current)
      }
    }
    tween.elapsed += dt
  }

  /** `_hiddenSequence.Kill()` — drop the instances and let the plain material back. */
  private endReveal(id: number): void {
    const tween = this.revealing.get(id)
    if (!tween) return
    this.revealing.delete(id)
    this.setBusClipped(id, this.clipped.get(id) ?? 0)
    for (const twin of tween.twins.values()) twin.dispose()
  }

  /**
   * Index one bus's drawn meshes and its clip-swap slots.
   *
   * Called once at build and **again after every rebuild**: `BusFleet.build`
   * makes fresh meshes, so anything cached against the old ones is dangling.
   * `node` is the marker subtree when it is being built for the first time;
   * afterwards both it and the art are found from the maps.
   */
  private recollectMeshes(bus: Bus, marker?: Object3D): void {
    const meshes: Mesh[] = []
    const swaps: BusMaterialSwap[] = []
    const roots = [marker ?? this.busNodes.get(bus.id), this.fleet?.drawnNode(bus) ?? null]
    for (const root of roots) {
      root?.traverse((child) => {
        const mesh = child as Mesh
        if (!mesh.isMesh) return
        meshes.push(mesh)
        if (this.canClip(mesh.material)) {
          swaps.push({ mesh, variants: [mesh.material, undefined, undefined, undefined] })
        }
      })
    }
    this.busMeshes.set(bus.id, meshes)
    this.busSwaps.set(bus.id, swaps)
  }

  /**
   * Per bus, every mesh it draws.
   *
   * A bus is two subtrees, not one — the marker box and the shipped art are
   * siblings under this view, so walking `busNode` alone misses the body.
   */
  private readonly busMeshes = new Map<number, Mesh[]>()

  /** `OriginalStartPercent` and `OriginalEndPercent` as arc distances. */
  private readonly roadWindow = new Vector2(0, 0)

  /** `?trace=body`, or `busBodyTrace(true)` from the console. */
  readonly bodyTrace = new BodyTrace()

  /** Pull every visual from gameplay state. `dt` only feeds the merge trace. */
  sync(dt = 0): void {
    this.cantMove.sync(dt)
    for (const bus of this.session.buses) {
      const node = this.busNodes.get(bus.id)
      if (!node) continue

      // `ToggleBusStates(false)` on an elevator layer that has not come up is a
      // plain `SetActive(false)`, so those buses are simply not there yet.
      if (bus.state === 'Gone' || !bus.active) {
        node.visible = false
        this.fleet?.hide(bus)
        continue
      }
      // A bus reaching either end of the drawn road is cut off there rather
      // than driving on across bare ground — see `RoadWindow`. All this decides
      // is whether the body reads its arc distance at all; *where* the cut
      // falls is the vertex own business, so this no longer flips between
      // frames and the material no longer swaps under a bus mid-tunnel.
      const mask = this.roadCutFor(bus)
      if (mask) this.clipped.set(bus.id, mask)
      else this.clipped.delete(bus.id)
      this.setBusClipped(bus.id, mask)

      // The headlight shafts are hidden **whole**, not cut. Their material is a
      // plain additive one with no clip planes, so once the body had been cut
      // away at a mouth the two beams went on glowing in the gap.
      //
      // ❓ **Whether the original hides the lamps too is an assumption, not a
      // reading — and this comment used to state it as one.** What is read:
      // `Bus.SetInvisibleObjectsHidden` (`__0x20fcf84.c`) walks
      // `invisibleObjects` and, per renderer, sets `gameObject.layer` and pushes
      // a `MaterialPropertyBlock` overriding `Bus.InvisibleBaseColorId` on
      // material slot 1. What is **not** read is which renderers are in that
      // list: `invisibleObjects` is a `[SerializeField]` (`dump.cs`:709411-709412)
      // and AssetRipper empties every serialized field on every MonoBehaviour,
      // so the prefabs carry no entries for it at all.
      //
      // Hiding the lamps with the body is the reading this port takes, because
      // a beam left glowing inside a tunnel is visibly wrong and the list is
      // named for exactly the objects that go invisible. But it is a choice made
      // in the absence of the data, and `tunnelInvisibleObjects.test.ts` freezes
      // the block so it reports itself if the list ever becomes readable.
      //
      // Asked at the head, because that is where the lamps are: the body may
      // still be half out of the mouth when the nose is well inside it. Two
      // distances, because the beam is 3.33 long and reaches *past* the nose —
      // the tip leaves the tarmac before the head does, and hiding on the head
      // alone would let it poke out through the cover first.
      if (this.fleet) {
        const follower = this.session.followerOf(bus)
        let lightsHidden = false
        if (follower && mask !== 0) {
          const head = follower.roadHeadDistance
          lightsHidden =
            this.outsideRoad(head) || this.outsideRoad(head + BUS_LIGHT_RAY.length)
        }
        this.fleet.setLightsHidden(bus, lightsHidden)
      }

      // Placement lives in the session, not here, so a headless check can see
      // exactly what the player sees. It used to live in this loop, which meant
      // `bus.position` was stale for anything on the road unless a renderer was
      // running — and a script asking "do these two buses overlap?" silently
      // measured parked positions.
      const follower = this.session.followerOf(bus)
      if (follower) this.session.placeOnCurve(bus, follower)

      node.position.copy(bus.position)
      node.quaternion.copy(bus.rotation)
      this.applyLotPose(node, bus)

      // The shipped body draws itself: rigid while parked, bent along whichever
      // curve it is riding otherwise. That split is the original's — riding a
      // spline switches the static renderers off and hands the body to
      // `BusBodyMesh`, which regenerates it against the curve.
      // `PlayFailCrash` rolls the bus onto its side. It is applied here, after
      // the body has been posed, because `RotateAround` in the original is a
      // **transform** operation on top of whatever the mesh already was — the
      // bus does not re-fit itself to the road while it is falling. (It cannot:
      // the first thing `PlayFailCrash` does is `this.enabled = false`.)
      const tip = this.tipOf(bus.id)

      // `Bus.SwitchColor` — the art was built against one colour and the bus is
      // now another. Rebuilding is the blunt way to swap every body and seat
      // material at once, and it is safe precisely where it happens: a reveal
      // lands while the bus is still `Parked`, before `rideTime` or the bend
      // state mean anything.
      //
      // 🔍 And `Bus.BreakIce` — same tail, same rebuild. `_type` goes to 0 at
      // `:37`/`:65` and the inlined `SwitchColor(_color)` then leaves the colour
      // material on slot 0 instead of `IceBus` (`palette.busBodyMaterials`).
      // The colour is unchanged, so the type has to be part of the key.
      const iced = bus.type === BoxTypeEnum.Ice
      const lookChanged =
        this.builtColour.get(bus.id) !== bus.visibleColor || this.builtIced.get(bus.id) !== iced
      if (lookChanged) {
        this.builtColour.set(bus.id, bus.visibleColor)
        this.builtIced.set(bus.id, iced)
      }
      if (this.fleet && lookChanged) {
        const stale = this.fleet.drawnNode(bus)
        stale?.parent?.remove(stale)
        const rebuilt = this.fleet.build(bus)
        if (rebuilt) this.object.add(rebuilt)
        // The old art's meshes are gone, so anything still holding them is
        // pointing at nothing. `busMeshes` feeds the rider clip count and
        // `busSwaps` feeds the tunnel material swap — left stale, a revealed bus
        // stops cutting at a mouth and the swap writes to removed meshes. This
        // was already true before the hidden-material work; it is fixed here
        // because that work is what first sends a *different* material through
        // the swap and so makes it visible.
        this.recollectMeshes(bus)
      }

      if (this.fleet?.has(bus)) {
        // Advance the ring sequence before posing, so the pose applies this
        // frame's weights rather than the previous frame's.
        // `LateUpdate`'s stop/start one-shot ends with, on both sides:
        //
        //     if (_state == BusSplineState.Entering)
        //         _bus.PauseAccommodationAnimation();   // .ResumeAccommodationAnimation()
        //
        // so a bus that has to wait for a gap while it is still on the entry
        // curve holds its ring wave where it is and picks it up when it rolls
        // again. Gated on `Entering`, not on being stopped: once the bus is on
        // the main road the wave runs whatever traffic does.
        this.fleet.advance(
          bus,
          dt,
          follower !== null && follower !== undefined,
          // `PlayFailCrash` opens with `_bus.PauseAccommodationAnimation()` too,
          // unconditionally — no `_state == Entering` gate on that one.
          tip !== null || (follower?.state === 'Entering' && !bus.ridersSwaying),
        )
        // 🔍 `ExitHiddenState`:86-133 — the reveal bounce, before the pose so
        // this frame's vertices carry it. Handed over every frame: a sequence
        // that has run out (or was killed) reads as 1 and the parts come back
        // to rest through the same path.
        this.fleet.setRevealScaleY(bus, this.revealScaleY(bus))
        // `poseBent` reports true the frame it builds a bus's seam twin — the
        // second copy drawn at the other tunnel mouth. Those meshes need the
        // clipping material swap too, and `busSwaps` is only rebuilt here.
        if (follower) {
          if (this.fleet.poseBent(bus, follower)) this.recollectMeshes(bus)
        } else {
          this.fleet.poseRigid(bus)
          // 🔴 **The punch and the lean have to be applied to the drawn art,
          // not only to the marker node.** `poseRigid` writes
          // `visual.node.quaternion = bus.rotation` into the *shipped* subtree,
          // which is a different `Object3D` from the `node` above — so a lot
          // pose applied only to `node` reached nothing the player can see once
          // an asset library was loaded. Both `ShakeObject`'s `DOPunchRotation`
          // and `MoveBoxToTarget`'s `DOLocalRotate` lean were invisible in the
          // browser for exactly that reason: the rules produced them every
          // frame (`bus.shakeEuler` peaks at 3.27° of the (1,3,3) punch) and the
          // renderer threw them away.
          const drawn = this.fleet.drawnNode(bus)
          if (drawn) this.applyLotPose(drawn, bus)
        }
      }

      // 🔍 `ExitHiddenState`:135 → :207-340 — after the rebuild (`SwitchColor`)
      // and after every other material decision this frame, so the reveal
      // instances are the last thing to land on the meshes. See `busRevealed`.
      this.advanceReveal(bus, dt)

      if (tip) {
        // Both subtrees: the shipped art, and the marker box that stands in for
        // it when no asset library is loaded.
        this.applyTip(node, tip)
        const drawn = this.fleet?.drawnNode(bus)
        if (drawn) this.applyTip(drawn, tip)
      }

      if (follower && this.trace.on) this.trace.sample(bus, follower, bus.position, dt)
      // Sampled after the pose, so it reads back what was actually drawn.
      if (follower && this.bodyTrace.on) this.bodyTrace.sample(bus, follower, this.fleet, dt)

      const body = this.busBodies.get(bus.id)
      if (body) (body.material as MeshLambertMaterial).color.copy(colorOf(bus.visibleColor))

      // The flag says which way this bus will pull out. Once it has, it is on
      // the road and the answer is no longer a question.
      //
      // 🔍 **And an iced bus does not show one (IC-2).** `Bus.SetFeature`
      // (`__0x20f85b0.c`) has three branches on the type, and the `Ice` one ends
      // `arrow.SetActive(SceneManager.GetActiveScene().name == "…")` — a string
      // IL2CPP stripped, so *that* line alone cannot say which way it goes.
      //
      // 📐 A second line settles it without the string: `Bus.BreakIce`
      // (`__0x20fb820.c`:55) is `SetActive(*(param_1 + 0x28), 1, 0)` — the
      // **arrow** (0x28, `dump.cs`:709398) switched on with a literal `1`, right
      // after `_type` is cleared. Turning it on when the ice breaks only means
      // anything if it was off while the ice was there. So the branch resolves
      // to `false` in the shipped scene whatever the string was, and the ❓ in
      // §10 is about the literal, not about the behaviour.
      const arrow = this.busArrows.get(bus.id)
      if (arrow) arrow.visible = arrowVisible(bus.occupiesLot, bus.type)

      // 🔍 The ice goes when the type does. `BusController.OnBusDeparted` chips
      // one off every iced bus and `Bus.BreakIce` clears `Type` at zero, so a
      // bus can thaw at any moment during play — nothing about it is decided at
      // build time. The material swap itself is the rebuild above (`builtIced`).

      // 🔍 `DecreaseIceCount`:37-44 writes the count after the decrement, and
      // `BreakIce`:38-48 turns the node off — so an empty string is exactly what
      // a thawed bus shows. Polled rather than event-driven, for the same reason
      // the rebuild above is: the type can change on any frame.
      const count = this.busIceCount.get(bus.id)
      if (count) count.setText(iced ? String(bus.iceBreakCount) : '')

      // The shower fires on the **edge**, the frame the ice material goes —
      // `BreakIce` plays it once, not while the bus is thawed. Read off the
      // type, since there is no shell whose visibility could carry it.
      {
        const was = this.iceWasShown.get(bus.id)
        if (was === true && !iced) this.fireIceBurst(bus)
        this.iceWasShown.set(bus.id, iced)
      }

      // 🔍 The countdown puff fires on a drop in the count, but **only while the
      // count is still above zero**. `DecreaseIceCount` guards both the sound
      // and this particle behind `if (0 < _iceBreakCount)` at :45; the shell
      // going is a different particle (0x90) fired at :152 on the other side of
      // that gate. So 3->2 puffs, 2->1 puffs, and 1->0 does not — it gets the
      // shower instead.
      //
      // This used to fire on every drop, and its comment stated that as the
      // original's behaviour ("the last chip plays this and then BreakIce plays
      // the shower"). Both particles went off together on the final chip.
      const countWas = this.iceCountWas.get(bus.id)
      if (countWas !== undefined && bus.iceBreakCount < countWas && bus.iceBreakCount > 0) {
        this.fireCountPuff(bus)
      }
      this.iceCountWas.set(bus.id, bus.iceBreakCount)
    }

    this.syncIceBurst(dt)
    this.syncCountPuff(dt, this.billboardCamera ?? undefined)
    this.syncBusParticles(dt, this.billboardCamera ?? undefined)
    this.syncCrashBurst(dt, this.billboardCamera ?? undefined)
    this.syncSkids(dt)
    this.syncPeople()
  }

  /**
   * Every passenger on screen, in one pass.
   *
   * Four populations, one batch: the queues, the waiting-area crowds, whoever
   * is mid-walk or mid-hop onto a bus, and the riders on the roof. They are all
   * the same `Stickman`, and all four are placed in **Unity space** — the space
   * the simulation works in — so this is the only place the conversion happens.
   */
  private syncPeople(): void {
    const people = this.people
    if (!people?.ready) return
    const t = this.session.tuning
    const scale = people.prefabScale * t.queueCharacterScale
    // `Char.obj` is 2.7104 wide, so a drawn body is 2.7104 * scale across and
    // half of that is how far a rider reaches past the point that stands in
    // for them in the tunnel test.
    const riderClipPad = (CHAR_MESH_WIDTH * scale) / 2
    people.begin()

    // -- queues ------------------------------------------------------------
    for (const queue of this.session.queues) {
      for (const entry of queue.entries) {
        queue.localPose(entry, _queuePose)
        _pos.copy(_queuePose.position).applyQuaternion(queue.rotation).add(queue.position)
        // `ApplyPose` leaves the rotation alone when the path direction is
        // degenerate, which only happens at a stationary sample.
        unityLookRotation(_queuePose.forward, _rot)
        _rot.premultiply(queue.rotation)
        people.add(entry.color, _pos, _rot, scale)
      }
    }

    // -- waiting areas -----------------------------------------------------
    //
    // 🔍 `WaitingArea.LateUpdate` writes a rotation **once** (`__0x2133d18.c`
    // :507, `set_localRotation`), and that write sits inside the branch opened
    // at :382 by `if ((entry.IsJumping & 1) == 0)`. So somebody still walking
    // in is skipped by the room entirely, and what is on their transform is
    // whatever `WalkAnimation`'s setter last put there — a world rotation, from
    // `Receive`'s `WalkAnimation` call. Only once they have landed does the
    // room take over with `Euler(TiltX, BaseRotY, TiltZ)`.
    for (const area of this.session.areas) {
      for (const member of area.crowd) {
        area.toWorld(member.visual, _pos)
        area.memberRotation(member, _rot)
        people.add(member.color, _pos, _rot, scale)
      }
    }

    // -- walking to the door, then hopping aboard ---------------------------
    //
    // 🔍 `TakeSettledColor__0x2135a58.c`:42 does `_crowd.RemoveAt(i)`, so from
    // the moment a passenger is picked for a bus the room's `LateUpdate` never
    // sees them again — nothing overwrites what `BoardOnePerson`'s
    // `WalkAnimation` (or `PerformBoardingJump`'s `JumpAnimation`) leaves on
    // the transform. They are drawn with their own wobble, all the way.
    for (const boarding of this.session.activeBoardings) {
      this.session.boardingWorld(boarding, _pos)
      this.session.boardingRotation(boarding, _rot)
      people.add(boarding.color, _pos, _rot, scale)
    }

    // -- thrown clear by the crash -----------------------------------------
    // `PlayCrashScatter` starts with `transform.SetParent(null)` and zeroes
    // both euler fields, so from that instant a scattered passenger is no
    // longer part of any bus: their own world position, their own rotation,
    // nothing borrowed from a seat. That is why they are drawn here and not in
    // the riding loop — by this point `bus.riders` is empty anyway,
    // `PlayFailCrash` cleared it.
    //
    // Before that instant they are still bolted on and turning over with the
    // bus, which is what `scatterWorld`/`scatterRotation` handle. Both live on
    // the session so a headless test can check them.
    for (const scatter of this.session.crashScatters) {
      this.session.scatterWorld(scatter, _pos)
      this.session.scatterRotation(scatter, _rot)
      people.add(scatter.color, _pos, _rot, scale)
    }

    // -- riding ------------------------------------------------------------
    for (const bus of this.session.buses) {
      const follower = this.session.followerOf(bus)
      if (!follower || bus.state === 'Gone') continue
      // Riders are culled one at a time against the same planes that cut the
      // body. They are drawn from one instanced batch per colour, shared across
      // every bus on screen, so a per-draw uniform cannot reach them — but a
      // passenger is small enough that dropping the whole person at the plane
      // reads the same as slicing them.
      const cutRiders = (this.clipped.get(bus.id) ?? 0) !== 0
      const headDistance = follower.roadHeadDistance

      // `BusSplineFollower.LateUpdate` runs one of two passenger updaters, and
      // they differ in more than cost:
      //
      //   moving  -> `UpdatePassengerPoses`: `GetPassengerSlotPose(i)` per
      //              passenger, so each faces the curve **at their own seat**.
      //   stopped -> `UpdatePassengerSwayOnly`: one `LookRotation` built from
      //              the follower's own heading, applied to everybody, and no
      //              position written at all.
      //
      // On a straight that is the same picture. On a bend it is not: a stopped
      // bus squares its whole busload to one heading, while a rolling one fans
      // them along the body. `swayHeading` is the direction the sway-only path
      // builds; what selects it is which of `LateUpdate`'s two early exits the
      // bus takes — held at a stop (0x48, the branch at line 104), or left with
      // no speed by the traffic factor (`bus.ridersSwaying`, the port's name
      // for `_wasTrafficStopped` at 0x15A).
      //
      // Positions need no special case: the original leaves them untouched, and
      // a stopped bus's slot poses do not move between frames anyway.
      let sharedRotation: Quaternion | null = null
      const swayOnly = follower.isHeldAtStop || !bus.ridersSwaying
      if (swayOnly && bus.riders.length > 0) {
        sharedRotation = unityLookRotation(follower.swayHeading(_tangent), _sharedRot)
      }

      for (let slot = 0; slot < bus.riders.length; slot++) {
        // `UpdatePassengerPoses`: posed against whichever curve the bus is on,
        // so a departing bus takes its passengers with it instead of leaving
        // them standing on the tarmac.
        const seat = follower.passengerSlotPose(slot, bus.capacity, t)
        _tangent.copy(seat.tangent)
        _tangent.y = 0
        if (_tangent.lengthSq() < 1e-9) _tangent.set(0, 0, 1)
        _tangent.normalize()

        // Vector3.Cross(Vector3.up, forward) is Unity's right.
        _pos.copy(seat.position)
        _pos.x += _tangent.z * seat.lateral
        _pos.z += -_tangent.x * seat.lateral
        _pos.y += t.passengerTopOffset
        // Half a body of slack, so the whole silhouette is behind the cover
        // before the head can clear its 4.01-tall roof. See `outsideRoad`.
        //
        // `seat.along` is the same offset back from the nose the mesh uses, so
        // a rider and the strip of bodywork under them are cut by one number.
        if (cutRiders && this.outsideRoad(headDistance - seat.along, riderClipPad)) continue

        if (sharedRotation) _rot.copy(sharedRotation)
        else unityLookRotation(_tangent, _rot)
        _rot.multiply(bus.riders[slot].swayRotation(_sway))
        people.add(bus.color, _pos, _rot, scale)
      }
    }

    people.end()
  }

  dispose(): void {
    for (const d of this.disposables) d.dispose()
    // Reveal instances are per bus and per sequence, owned here, not by the
    // library — a level torn down mid-reveal would otherwise leak them.
    for (const id of [...this.revealing.keys()]) this.endReveal(id)
    // `fleet` and `people` are built in the constructor and were never let go,
    // so every level left its buses' geometry on the GPU. `BusFleet` clones one
    // geometry per body part per bus (`busView.ts`:1129 — the clone is what
    // carries the baked part transform, so it cannot be shared), which made
    // this the largest allocation a level owns by a wide margin.
    //
    // Measured with `?cycle=2` before the fix: 9488 -> 19132 -> 28776
    // geometries, +9644 per pass over all 40 levels and dead flat, i.e. a leak
    // rather than a warm-up. Textures never moved, which is what pointed here:
    // the library owns those and hands them out, so only geometry is per-level.
    this.fleet?.dispose()
    this.people?.dispose()
    // Named as well as tracked: the label owns a `CanvasTexture` per level, and
    // `viewDisposal.test.ts` asks for the name so a future reader sees it here
    // rather than having to trace `disposables`.
    this.cantMove.dispose()
    for (const label of this.busIceCount.values()) label.dispose()
    this.busIceCount.clear()
    this.object.removeFromParent()
  }
}

/**
 * Rotation whose local -Z points along `forward`.
 *
 * Built from an explicit basis rather than `setFromUnitVectors`, which picks
 * the shortest arc between two vectors and can roll the bus about its own axis
 * on a curve. Columns are (right, up, back), where back is -forward because
 * three.js objects face -Z.
 */
const _right = new Vector3()
const _back = new Vector3()
const _basis = new Matrix4()
const _quat = new Quaternion()

export function orientAlong(forward: Vector3): Quaternion {
  _right.crossVectors(forward, UP).normalize()
  if (_right.lengthSq() < 1e-9) _right.set(1, 0, 0)
  _back.copy(forward).negate()
  _basis.makeBasis(_right, UP, _back)
  return _quat.setFromRotationMatrix(_basis)
}
